import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import DuoButton from './DuoButton';
import { PALETTE, RADIUS } from '../theme';
import { Product } from '../data/products';
import { useT } from '../lib/i18n';

const STATUS_STYLE: Record<Product['status'], { bg: string; color: string }> = {
  live: { bg: PALETTE.greenSoft, color: PALETTE.greenDepth },
  beta: { bg: PALETTE.signalSoft, color: PALETTE.signalDepth },
  soon: { bg: PALETTE.groundSunken, color: PALETTE.inkMuted },
};

/**
 * A product tile.
 *
 * Built like a Duolingo lesson card: a 2px hairline border, a 16px radius and a
 * 4px solid bottom edge, so the whole card is visibly the same kind of object as
 * the buttons on it. Hovering lifts it onto a taller edge — the same gesture the
 * buttons use, one step quieter.
 */
export default function ProductCard({ product }: { product: Product }) {
  const t = useT();
  const { Logo, status } = product;
  const copy = t.product[product.id];
  const statusStyle = STATUS_STYLE[status];

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        p: { xs: 3, md: 4 },
        borderRadius: `${RADIUS.card}px`,
        border: `2px solid ${PALETTE.hairline}`,
        backgroundColor: PALETTE.ground,
        boxShadow: `0 4px 0 0 ${PALETTE.hairline}`,
        transition: 'transform 200ms ease, box-shadow 200ms ease, border-color 200ms ease',
        '@media (hover: hover)': {
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: `0 8px 0 0 ${PALETTE.hairline}`,
            borderColor: PALETTE.inkFaint,
          },
        },
      }}
    >
      <Stack direction="row" alignItems="flex-start" spacing={2.5} sx={{ mb: 3 }}>
        {/* The mark keeps its own brand colours; the tile does not recolour it. */}
        <Box sx={{ flexShrink: 0, borderRadius: '22%', overflow: 'hidden', boxShadow: '0 3px 0 0 rgba(0,0,0,0.10)' }}>
          <Logo size={72} />
        </Box>

        <Box sx={{ minWidth: 0, pt: 0.5 }}>
          <Typography variant="h5" sx={{ fontSize: { xs: 22, md: 25 }, fontWeight: 800, color: PALETTE.ink }}>
            {product.name}
          </Typography>
          <Typography sx={{ mt: 0.5, fontSize: 15, fontWeight: 700, color: PALETTE.inkMuted }}>
            {copy.tagline}
          </Typography>
        </Box>
      </Stack>

      <Typography sx={{ fontSize: 16, color: PALETTE.inkMuted, mb: 3 }}>{copy.description}</Typography>

      {/* Held at button height so the footer rows line up across cards whether or
          not a card has a button in it. */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mt: 'auto', minHeight: 48, flexWrap: 'wrap', gap: 1.5 }}>
        {/* No button before there is a site to send anyone to — a disabled control
            next to a "coming soon" badge just says the same thing twice. */}
        {product.url && (
          <DuoButton component="a" href={product.url} target="_blank" rel="noopener noreferrer">
            {t.products.visit}
          </DuoButton>
        )}

        {/* Status carries its own word, so it never depends on colour alone. */}
        <Box
          sx={{
            px: 1.75,
            py: 0.75,
            borderRadius: `${RADIUS.pill}px`,
            fontSize: 12,
            fontWeight: 800,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            backgroundColor: statusStyle.bg,
            color: statusStyle.color,
          }}
        >
          {t.products.status[status]}
        </Box>

        <Box sx={{ fontSize: 13, fontWeight: 700, color: PALETTE.inkFaint }}>{product.since}</Box>
      </Stack>
    </Box>
  );
}
